import React from "react";
import { BaseProps } from "../../../shared/props";
import { ComponentValidator, validateComponent } from "../../../shared/validation";
import { gutterSize } from "../../../styles/constants";
import { TextAlignment } from "../../../styles/fonts";
import { Stack, StackItem, StackItemProps, StackProps } from "../Stack/Stack";

interface SharedSlotStackProps extends BaseProps {
    /**
     * Children to render inside.
     */
    readonly children: React.ReactNode;
    /**
     * Height for the Stack/Item
     */
    readonly height?: StackProps["height"];
    /**
     * Alignment of the text within the stack
     */
    readonly textAlignment?: TextAlignment;
}

export interface SlotStackProps extends SharedSlotStackProps {
    /**
     * Margin to be applied to the Stack/Item relative to its container.
     */
    readonly margin?: StackProps["margin"];
    /**
     * Padding to be applied to the Stack/Item contents relative to its border.
     */
    readonly padding?: StackProps["padding"];
    /**
     * Maximum height for the Stack.
     */
    readonly maxHeight?: StackProps["maxHeight"];
    /**
     * Overflow behavior of the Stack.
     */
    readonly overflow?: StackProps["overflow"];
    /**
     * Whether to render Stack children in the opposite direction (bottom-to-top if it's a vertical Stack and
     * right-to-left if it's a horizontal Stack).
     */
    readonly reversed?: StackProps["reversed"];
    /**
     * How to align Stack children vertically (along the y-axis).
     */
    readonly verticalAlign?: Extract<StackProps["verticalAlign"], "start" | "end" | "center" | "stretch">;
    /**
     * Whether to render a border around Stack
     */
    readonly withBorder?: StackProps["withBorder"];
    /**
     * How to align Stack children horizontally (along the x-axis). Applicable when not all slots are used.
     */
    readonly horizontalAlign?: StackProps["horizontalAlign"];

    /**
     * Number of total slots to allocate to this specific slot stack.
     * Any number of slots passed to the child SlotStackItem will be a percentage of this amount.
     */
    readonly totalSlots: number;
    /**
     * Background color of the stack
     */
    readonly backgroundColor?: StackProps["backgroundColor"];
}

export interface SlotStackItemProps extends SharedSlotStackProps {
    /**
     * How to align the StackItem along the y-axis, relative to its container.
     */
    readonly align?: StackItemProps["align"];

    /**
     * Number of slots to allocate to this specific SlotStackItem.
     */
    readonly slots: number;
}

const totalSlotsValidator = (addedSlots: number, totalSlots: number): ComponentValidator => (
    () => (addedSlots > totalSlots ? `The total number of slots passed to the SlotStackItems (${addedSlots})
    is greater than the total passed to the SlotStack (${totalSlots})` : undefined)
);

const slotStackChildrenValidator = (child: React.ReactChild | React.ReactFragment | React.ReactPortal): ComponentValidator => (
    () => (!React.isValidElement<SlotStackItemProps>(child) || child.type !== SlotStackItem ?
        "SlotStack only accepts children of type SlotStackItem." : undefined)
);

/**
 * Add props to StackItem children to match the given slots
 * @param stack An array of StackItem React element
 * @param totalSlots The total slots; if not provided, the total is the sum of the slots for each item
 */
export function withSlots(stackItems: readonly React.ReactElement<SlotStackItemProps>[], totalSlots: number) {
    const addedSlots = stackItems.reduce((iter, item) => iter + item.props.slots, 0);

    validateComponent("SlotStack", [totalSlotsValidator(addedSlots, totalSlots)]);

    return stackItems.map((child) => {
        const { slots, ...otherProps } = child.props;

        const ratio = slots / totalSlots;

        // If we calculate the slot size ratios including the gutter size, everything lines up. Take an example where the box
        // width is 60, gutter is 6, and total slots is 3. The width of each slot is 16, and the width plus gutter is 22. For an
        // item that takes two slots, the width needs to be 38. That's not twice 16, but plus a gutter it's 44, which is twice 22.
        // Although a left-most gutter is used in the ratio calculations, it is subtracted at the end since it's not displayed.
        //    6  |------16------|   6  |------16------|   6  |------16------|
        //    6  |-----------------38-----------------|   6  |------16------|

        // The base formula is ((100% + gutter) * ratio) - gutter
        // This is expanded below to (100% * ratio) - (gutter * (1 - ratio))

        const widthPercent = 100 * ratio;     // tslint:disable-line: no-magic-numbers
        const gutterAdjust = gutterSize * (1 - ratio);

        const slotWidth = `calc(${widthPercent}% - ${gutterAdjust}px)`;

        return <StackItem {...otherProps} width={slotWidth} key={child.key || undefined} />;
    });
}

// Deferring all SlotStackItem manipulation to the SlotStack component,
// since it has a more general context on all of its children.
export const SlotStackItem: React.FC<SlotStackItemProps> = () => (<></>);

export const SlotStack: React.FC<SlotStackProps> = ({
    children,
    ...otherStackProps
}) => {
    //Added this check to enable conditional rendering of SlotStackItem
    const mappedChildren = React.Children.toArray(children).filter(child => !!child).map(child => {
        validateComponent("SlotStack", [slotStackChildrenValidator(child)]);

        return <StackItem {...(child as any).props} key={(child as any).key || undefined} />;
    });

    // Stack will take care of setting the correct stack props and calling "withSlots"
    return <Stack type="slots" {...otherStackProps} children={mappedChildren} />;
};
